/**
 * DeleteAccountModal — in-app account deletion (Apple App Store 5.1.1).
 *
 * Requires the current password + typing DELETE to confirm. Calls
 * DELETE /api/auth/me (anonymises the account server-side), then logs out and
 * returns to the login screen.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../services/api.js'
import { useAuthStore } from '../../stores/auth.js'

export default function DeleteAccountModal({ onClose }) {
  const { logout } = useAuthStore()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmText, setConfirmText] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  async function submit(e) {
    e.preventDefault()
    setErr(null)
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      setErr('Type DELETE to confirm.')
      return
    }
    if (!password) { setErr('Enter your password.'); return }
    setBusy(true)
    try {
      await api.delete('/auth/me', { password })
      logout()
      navigate('/login')
    } catch (e2) {
      setErr(e2.message || 'Could not delete your account')
      setBusy(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal request-research-modal" onClick={e => e.stopPropagation()}>
        <header className="modal__header">
          <h2>Delete account</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <form onSubmit={submit} className="request-research-modal__body">
          <p className="request-research-modal__intro">
            This permanently deletes your account and removes your personal data.
            Your past contributions stay in the shared archive but are no longer
            linked to you. <strong>This cannot be undone.</strong>
          </p>
          <label className="request-research-modal__field">
            <span>Your password</span>
            <input
              type="password" value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••" autoComplete="current-password"
            />
          </label>
          <label className="request-research-modal__field">
            <span>Type <strong>DELETE</strong> to confirm</span>
            <input
              type="text" value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="DELETE"
            />
          </label>
          {err && <div className="request-research-modal__err">{err}</div>}
          <div className="request-research-modal__actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-danger" disabled={busy}>
              {busy ? 'Deleting…' : 'Delete my account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
